import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { logger } from "@/utils/logger";

const AIEW_BASE_URL = import.meta.env.VITE_AIEW_URL || "https://ai-edge.io";

interface FeatureFlagResult {
  value: boolean;
  status: "loading" | "loaded" | "error";
}

async function fetchFlag(flagName: string): Promise<boolean> {
  const response = await fetch(
    `${AIEW_BASE_URL}/api/feature-flags/${flagName}`,
    { credentials: "include" }
  );
  // 404 = flag not found, treat as disabled
  if (response.status === 404) {
    return false;
  }
  // Other non-2xx = server error, let React Query retry
  if (!response.ok) {
    throw new Error(`Feature flag fetch failed: ${response.status}`);
  }
  const data = await response.json();
  return Boolean(data.value);
}

export function useFeatureFlag(flagName: string): FeatureFlagResult {
  const hasLoggedError = useRef(false);

  const { data, status, error } = useQuery({
    queryKey: ["feature-flag", flagName],
    queryFn: () => fetchFlag(flagName),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  if (error && !hasLoggedError.current) {
    logger.warn(`Feature flag "${flagName}" fetch failed, defaulting to false`, error);
    hasLoggedError.current = true;
  }

  return {
    value: data ?? false,
    status: status === "pending" ? "loading" : status === "error" ? "error" : "loaded",
  };
}
